/* globals FakeTouchEvent */
import { DOCUMENT } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { BrowserService } from '@core/browser.service';
import { Orientation } from '@shared/viewer/enums/orientation';
import * as _ from 'lodash';

import { DrawingBrushSizes, DrawingColors, DrawingPage, DrawingTools, DrawingVectors } from './drawing';

@Component({
  selector: 'zbp-drawing',
  templateUrl: './drawing.component.html',
  styleUrls: ['./drawing.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class DrawingComponent implements OnInit, OnChanges {
  private _drawing = false;
  private _context: CanvasRenderingContext2D;
  private _vectors: DrawingVectors;

  @Input() id: string = '';
  @Input() tool: DrawingTools = DrawingTools.none;
  @Input() color: DrawingColors = DrawingColors.black;
  @Input() brushSize: DrawingBrushSizes = DrawingBrushSizes.med;
  @Input() orientation: Orientation = Orientation.None;
  @Input() page: DrawingPage;
  @Input() background: HTMLImageElement = null;
  @Input() zoomLevel: number;
  @Output() drawingStack: EventEmitter<DrawingPage> = new EventEmitter<DrawingPage>();
  @Output() undoStack = new EventEmitter();
  @ViewChild('canvas', { static: true }) private canvasRef: ElementRef;

  constructor(
    private browser: BrowserService,
    @Inject(DOCUMENT) private document: Document,
    private renderer: Renderer2,
  ) {
    if (this.browser.isTablet) {
      this.renderer.addClass(this.document.body, 'disableTabletOverscroll');
    }
  }

  ngOnInit() {
    this.zoomLevel = this.zoomLevel ?? 100;

    if (!this.id) {
      this.id = _.uniqueId('pageCanvas');
    }

    if (!this.page) {
      this.page = new DrawingPage();
    }

    // Initialize vectors and either set background and resize, or just resize so that the canvas element
    // gets width/height set based on its parent element.
    this.initializeVectors();
    if (this.background) {
      this.setBackgroundImage();
    } else {
      this.resizeCanvas();
    }

    this.drawingStack.emit(this.page);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.page && !changes.page.isFirstChange() && changes.page.currentValue) {
      this.initializeVectors();
      this.resizeCanvas();
    }
    if (changes.background && !changes.background.isFirstChange() && changes.background.currentValue) {
      this.initializeVectors();
      this.setBackgroundImage();
    }
    if (changes.zoomLevel && !changes.zoomLevel.isFirstChange() && changes.zoomLevel.currentValue) {
      this.initializeVectors();
      this.resizeCanvas();
    }
  }

  get context(): CanvasRenderingContext2D {
    if (!this._context) {
      this._context = this.canvas.getContext('2d');
    }
    return this._context;
  }

  get canvas(): HTMLCanvasElement {
    return this.canvasRef.nativeElement;
  }

  @HostListener('window:resize', ['$event'])
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onResize(event: UIEvent) {
    this.resizeCanvas();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  touchend(e: TouchEvent | FakeTouchEvent) {
    if (this.browser.isTablet) {
      this._drawing = false;
    }
  }

  touchdown(e: TouchEvent | FakeTouchEvent) {
    if (this.browser.isTablet) {
      const touchPosition = this.getTouchPosition(this.canvas, e);
      this._drawing = true;
      this.addClick(touchPosition.x, touchPosition.y, false);
      this.undoStack.emit(this.page.clickX.length);
      this.draw();
    }
  }

  touchmove(e: TouchEvent | FakeTouchEvent) {
    if (this.browser.isTablet) {
      if (this.isTabletDrawing()) {
        // Disables all default/parent touch events, which is what pointer-events is supposed to do.
        e.preventDefault();
      }

      if (this._drawing && this.tool !== DrawingTools.none) {
        const touchPosition = this.getTouchPosition(this.canvas, e);
        this.addClick(touchPosition.x, touchPosition.y, true);
        this.draw();
      }
    }
  }

  /**
   * When the user clicks on canvas we record the position in an array via the addClick function.
   *
   * We set the boolean paint to true (we will see why in a sec).
   * Finally, we update the canvas with the function redraw
   */
  mousedown(e: MouseEvent) {
    if (!this.browser.isTablet) {
      this.undoStack.emit(this.page.clickX.length);
      this._drawing = true;
      const { x, y } = this.convertElementPositionToImagePosition(e.offsetX, e.offsetY);
      this.addClick(x, y, false);
      this.draw();
    }
  }

  /**
   * Just like moving the tip of a marker on a sheet of paper.
   *
   * We want to draw on the canvas when our user is pressing down.
   * The boolean paint will let us know if the virtual marker is pressing down on the paper or not.
   * If paint is true, then we record the value. Then redraw.
   */
  mousemove(e: MouseEvent) {
    if (!this.browser.isTablet && this._drawing && this.tool !== DrawingTools.none) {
      const { x, y } = this.convertElementPositionToImagePosition(e.offsetX, e.offsetY);
      this.addClick(x, y, true);
      this.draw();
    }
  }

  private convertElementPositionToImagePosition(x, y): {x: number, y: number} {
    return {
      x: (x / this.canvas.clientWidth) * this.background.naturalWidth,
      y: (y / this.canvas.clientHeight) * this.background.naturalHeight,
    };
  }

  mouseover(e: MouseEvent) {
    if (e.buttons === 1) {
      const touchPosition = this.getTouchPositionMouse(this.canvas, e);
      this._drawing = true;
      this.addClick(touchPosition.x, touchPosition.y, false);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  mouseup(e: MouseEvent) {
    if (!this.browser.isTablet) {
      this._drawing = false;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  mouseleave(e: MouseEvent) {
    if (!this.browser.isTablet) {
      this._drawing = false;
    }
  }

  isTabletDrawing(): boolean {
    return this.browser.isTablet && this._drawing;
  }

  isDrawing(): boolean {
    return this._drawing;
  }

  isActive(): boolean {
    return this.tool !== DrawingTools.none;
  }

  clear() {
    this.context.clearRect(0, 0, this.context.canvas.width, this.context.canvas.height);
    this.page = new DrawingPage();
    this.drawingStack.emit(this.page);
  }

  draw() {
    // Clear the canvas.
    this.context.clearRect(0, 0, this.context.canvas.width, this.context.canvas.height);

    this.context.lineJoin = 'round';

    for (let i = 0; i < this.page.clickX.length; i++) {
      this.context.beginPath();
      this.context.globalCompositeOperation = this.page.clickErase[i] ? 'destination-out' : 'source-over';
      this.context.lineWidth = this.page.clickSize[i];

      if (this.page.clickDrag[i] && i) {
        this.context.moveTo(this.page.clickX[i - 1], this.page.clickY[i - 1]);
      } else {
        this.context.moveTo(this.page.clickX[i] - 1, this.page.clickY[i]);
      }
      this.context.lineTo(this.page.clickX[i], this.page.clickY[i]);
      this.context.closePath();
      this.context.strokeStyle = this.page.clickColor[i];
      this.context.stroke();
    }
  }

  private addClick(x: number, y: number, dragging: boolean) {
    const erase = this.tool === DrawingTools.erase;

    this.page.clickX.push(x);
    this.page.clickY.push(y);
    this.page.clickDrag.push(dragging);
    this.page.clickColor.push(this.color);
    this.page.clickErase.push(erase);
    this.page.clickSize.push(this.brushSize);
    this.drawingStack.emit(this.page);
  }

  private getTouchPosition(canvasRef: HTMLCanvasElement, touch: TouchEvent | FakeTouchEvent) {
    const rect = canvasRef.getBoundingClientRect();
    return this.convertElementPositionToImagePosition(
      touch.touches[0].clientX - rect.left,
      touch.touches[0].clientY - rect.top
    );
  }

  private getTouchPositionMouse(canvasRef: HTMLCanvasElement, mouse: MouseEvent) {
    const rect = canvasRef.getBoundingClientRect();
    return this.convertElementPositionToImagePosition(
      mouse.clientX - rect.left,
      mouse.clientY - rect.top
    );
  }

  private resizeCanvas(): void {
    // The canvas' internal measurements match the background image's natural dimensions
    this.canvas.width = this.background.naturalWidth;
    this.canvas.height = this.background.naturalHeight;

    this.draw();
  }

  /**
   * Initializes DrawingVectors.
   *
   * @todo Denis should document this or the DrawingVectors class.
   */
  private initializeVectors(): void {
    this._vectors = new DrawingVectors();
    this._vectors.InitWidth = this.canvas.clientWidth;
    this._vectors.InitHeight = this.canvas.clientHeight;
  }

  private setBackgroundImage(): void {
    if (this.background) {
      this.canvas.style['background-image'] = `url(${this.background.src})`;
      this._vectors = new DrawingVectors();
      this._vectors.InitWidth = this.canvas.clientWidth;
      this._vectors.InitHeight = this.canvas.clientHeight;
      this.resizeCanvas();
    }
  }
}
